<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12">
        <v-card elevation="4">
          <v-card-title class="text-indigo text-button">Crear llave de sistema</v-card-title>

          <v-form ref="formRef" @submit.prevent="save">
            <v-container fluid>
              <v-row>
                <v-col cols="12">
                  <h4 class="text-button text-primary">Datos de la llave</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" sm="6" lg="4">
                        <v-select
                          v-model="data.type"
                          :items="TYPES"
                          label="Tipo de llave"
                          prepend-inner-icon="mdi-shape"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('type')"
                          hint="TRANSPORTE se usa para exportar material; ENCAPSULAMIENTO para envolver llaves."
                          persistent-hint
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-text-field
                          v-model="data.alias"
                          label="Alias (opcional)"
                          prepend-inner-icon="mdi-tag"
                          :disabled="saving"
                          :error-messages="errors.for('alias')"
                          @update:model-value="errors.clear('alias')"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-switch
                          v-model="data.default"
                          color="success"
                          inset
                          :disabled="saving"
                          label="Marcar como llave por defecto"
                          hint="Sólo puede haber una llave por defecto de cada tipo."
                          persistent-hint
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>
              </v-row>
            </v-container>

            <v-card-actions>
              <v-spacer />
              <v-btn type="submit" variant="text" color="success" class="mx-1" :loading="saving">
                <v-icon class="mr-1">mdi-content-save</v-icon>Crear
              </v-btn>
              <v-btn
                variant="text"
                color="error"
                class="mx-1"
                :disabled="saving"
                @click="router.push('/system/keys')"
              >
                Cancelar
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
/**
 * Alta de llave de sistema. SÓLO alta.
 *
 * La versión anterior tenía también modo edición y llamaba a
 * `system.keys.get(Number(route.params.id))`, pero ese método pega contra
 * `GET /system/keys/{type}`, cuyo path liga el enum SystemKeyType: pasarle un id
 * numérico devuelve 400. **No existe `GET /system/keys/{id}`**, así que ese modo
 * edición nunca pudo funcionar.
 *
 * Lo único modificable de una llave de sistema es su marca de "default"
 * (`PUT /system/keys/{id}`), y eso se resuelve como acción de fila en el índice.
 */
import { ref } from "vue";
import { useRouter } from "vue-router";
import type { SystemKeyType } from "bancojs";
import { banco } from "@/api/client";
import { useApiErrors } from "@/composables/useApiErrors";
import { useFormErrors } from "@/composables/useFormErrors";
import { useNotificationsStore } from "@/stores/notifications";
import { systemKeyTypeLabel } from "@/utils/labels";

const router = useRouter();
const { report } = useApiErrors();
const notifications = useNotificationsStore();
const errors = useFormErrors();

const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);

const TYPES = (["TRANSPORT", "WRAPPING"] as SystemKeyType[]).map((t) => ({
  title: systemKeyTypeLabel(t),
  value: t,
}));

const saving = ref(false);
const data = ref({
  type: "TRANSPORT" as SystemKeyType,
  alias: "",
  default: false,
});

const rules = { required: (val: unknown) => !!val || "Campo requerido." };

async function save() {
  const validation = await formRef.value?.validate();
  if (validation && !validation.valid) return;

  errors.reset();
  saving.value = true;
  try {
    const created = await banco.system.keys.create({
      type: data.value.type,
      alias: data.value.alias || undefined,
      default: data.value.default,
    });
    notifications.success(`Se creó la llave de sistema ${created.key_id}.`, "Creado");
    router.push("/system/keys");
  } catch (err) {
    report(err, {
      title: "Llaves de sistema",
      fallback: "Ocurrió un error al crear la llave de sistema.",
      form: errors,
    });
  } finally {
    saving.value = false;
  }
}
</script>
